import React, { useEffect, useRef } from 'react';
import lottie from 'lottie-web';
import animationData from './ibm-journey-animation.json';

import HOME from '../../data/home';

import {
  _setTransitioning,
  _setHomeNavIn,
  _setAnimRight,
  _setInfocardIn,
} from '../../store/actionCreators';

import { useSelector, useDispatch } from 'react-redux';
import { getScene } from '../../store/selectors';

const Animation = () => {
  let anim = useRef(null);
  let observer = useRef(null);
  const lottieContainer = useRef(null);
  const home = useSelector((state) => state.home);
  const scene = useSelector(getScene);
  const dispatch = useDispatch();

  const setTransitioning = (bool) => {
    dispatch(_setTransitioning(bool));
  };
  const setHomeNavIn = (bool) => {
    dispatch(_setHomeNavIn(bool));
  };
  const setAnimRight = (bool) => {
    dispatch(_setAnimRight(bool));
  };
  const setInfocardIn = (bool) => {
    dispatch(_setInfocardIn(bool));
  };

  function usePrevious(val) {
    const ref = useRef();
    useEffect(() => {
      ref.current = val;
    });
    return ref.current;
  }
  const previousScene = usePrevious(scene);

  const useLottie = () => {
    useEffect(() => {
      anim.current = lottie.loadAnimation({
        container: lottieContainer.current,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        animationData,
      });

      setTransitioning(true);

      // init lottie
      anim.current.addEventListener('DOMLoaded', () => {
        anim.current.playSegments(HOME.in, true);

        const onEnterFrame = () => {
          if (anim.current.firstFrame === 0) {
            if (HOME.labelIn < anim.current.currentFrame) {
              setHomeNavIn(true);
            }
            if (HOME.infocardIn < anim.current.currentFrame) {
              setInfocardIn(true);

              // remove listener inside the last event (HOME.labelIn < HOME.infocardIn)
              anim.current.removeEventListener('enterFrame', onEnterFrame);
            }
          }
        };
        anim.current.addEventListener('enterFrame', onEnterFrame);

        const onComplete = () => {
          setTransitioning(false);
          anim.current.removeEventListener('complete', onComplete);
        };
        anim.current.addEventListener('complete', onComplete);

        // pause lottie when out of view
        const callback = (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) anim.current.play();
            else anim.current.pause();
          });
        };

        observer = new IntersectionObserver(callback, {
          rootMargin: '0px',
        });

        observer.observe(lottieContainer.current);
      });

      return () => {
        observer.unobserve(lottieContainer.current);
      };
    }, []);
  };
  useLottie();

  const useFromHome = () => {
    const transitioningFromHomeToScene = !previousScene && scene;
    useEffect(() => {
      const onComplete = () => {
        setTransitioning(false);
        setInfocardIn(true);
        anim.current.loop = true;
        anim.current.playSegments(scene.loop);
        anim.current.removeEventListener('complete', onComplete);
      };
      if (transitioningFromHomeToScene) {
        anim.current.loop = false;
        setTransitioning(true);
        setInfocardIn(false);
        anim.current.addEventListener('complete', onComplete);
        anim.current.playSegments(scene.fromHome);
      }
      // eslint-disable-next-line
    }, [home]);
  };
  useFromHome();

  const useSceneTransitions = () => {
    const transitioningBetweenScenes = previousScene && scene;
    const onComplete = () => {
      setTransitioning(false);
      anim.current.loop = true;
      anim.current.playSegments(scene.loop);
      anim.current.removeEventListener('complete', onComplete);
    };
    const onEnterFrame = () => {
      if (anim.current.firstFrame === scene.in[0]) {
        setInfocardIn(true);
        anim.current.removeEventListener('enterFrame', onEnterFrame);
      }
    };
    useEffect(() => {
      if (transitioningBetweenScenes) {
        anim.current.loop = false;
        setTransitioning(true);
        setInfocardIn(false);

        anim.current.addEventListener('complete', onComplete);
        anim.current.addEventListener('enterFrame', onEnterFrame);

        anim.current.playSegments(previousScene.out);
        anim.current.playSegments(scene.in);
      }
      // eslint-disable-next-line
    }, [scene]);
  };
  useSceneTransitions();

  const useHomeIn = () => {
    const onComplete = () => {
      setTransitioning(false);
      anim.current.removeEventListener('complete', onComplete);
    };
    const onEnterFrame = () => {
      if (anim.current.firstFrame === 0) {
        setAnimRight(true);

        if (HOME.labelIn < anim.current.currentFrame) {
          setHomeNavIn(true);
        }
        if (HOME.infocardIn < anim.current.currentFrame) {
          setInfocardIn(true);
          anim.current.removeEventListener('enterFrame', onEnterFrame);
        }
      }
    };
    useEffect(() => {
      if (home && previousScene) {
        anim.current.loop = false;
        setTransitioning(true);
        setInfocardIn(false);

        anim.current.addEventListener('complete', onComplete);
        anim.current.playSegments(previousScene.out);
        anim.current.playSegments(HOME.in);

        anim.current.addEventListener('enterFrame', onEnterFrame);
      }
      if (!home) {
        setHomeNavIn(false);
        setAnimRight(false);
      }
      // eslint-disable-next-line
    }, [home]);
  };

  useHomeIn();

  return (
    <>
      <div id="lottie-container" ref={lottieContainer} />
    </>
  );
};

export default Animation;
